import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Map, Layers, AlertCircle, CheckCircle, Edit, Save, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useLayout } from '../../contexts/LayoutContext';
import { MOCK_STATIONS } from '../../utils/mockStationsData';
import { buildFarmGrouping, getSectionIdsForDrill, resolveSectionIdsForStation } from '../../utils/areaOverlayUtils';
import { CompactMapView } from './CompactMapView';
import { HierarchicalSelector } from './HierarchicalSelector';
import { Button } from '../ui';
import './StationSelectionHeader.css';

export function StationSelectionHeader({
  selectedSections,
  selectAllSections,
  onSectionsChange,
  onSelectAllChange,
  mapExpanded: externalMapExpanded,
  mapClosing: externalMapClosing,
  onMapToggle: externalOnMapToggle,
  showActions = true,
  showViewToggle = true,
  hideTreeView = false,
  showUnsavedWarning = true,
  onUnsavedChangesChange,
}) {
  const { t } = useTranslation();
  const { selectedFarm, drillState, setDrillState } = useAnalytics();
  const { selectedFarm: layoutFarm } = useLayout();
  const [internalMapExpanded, setInternalMapExpanded] = useState(false);
  const [internalMapClosing, setInternalMapClosing] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);
  const [isEditingSelection, setIsEditingSelection] = useState(true);
  const lastSyncedSelectionRef = React.useRef(null);
  const lastFarmIdRef = React.useRef(null);
  
  // Use external state if provided, otherwise use internal state
  const mapExpanded = externalMapExpanded !== undefined ? externalMapExpanded : internalMapExpanded;
  const mapClosing = externalMapClosing !== undefined ? externalMapClosing : internalMapClosing;
  const defaultMapView = true;

  React.useEffect(() => {
    if (!selectedFarm?.id) return;
    if (lastFarmIdRef.current === selectedFarm.id) return;
    lastFarmIdRef.current = selectedFarm.id;
    setPendingSelection(null);
    lastSyncedSelectionRef.current = null;
    setIsEditingSelection(true);
    if (externalMapExpanded === undefined) {
      setInternalMapExpanded(defaultMapView);
      setInternalMapClosing(false);
    }
  }, [externalMapExpanded, selectedFarm?.id, defaultMapView]);

  const setMapView = (shouldShowMap) => {
    if (externalOnMapToggle) {
      externalOnMapToggle(shouldShowMap);
      return;
    }
    if (shouldShowMap) {
      setInternalMapExpanded(true);
      setInternalMapClosing(false);
      return;
    }
    if (!internalMapExpanded) {
      setInternalMapClosing(false);
      return;
    }
    if (!internalMapClosing) {
      setInternalMapClosing(true);
      setTimeout(() => {
        setInternalMapExpanded(false);
        setInternalMapClosing(false);
      }, 450);
    }
  };

  // Create stable reference for selectedSections to avoid dependency array size changes
  const selectedSectionsStable = useMemo(() => {
    return Array.isArray(selectedSections) ? selectedSections : [];
  }, [selectedSections]);

  const selectionSnapshot = useMemo(() => {
    return {
      selectAll: selectAllSections,
      sections: [...selectedSectionsStable],
    };
  }, [selectAllSections, selectedSectionsStable]);

  const selectionsEqual = (a, b) => {
    if (!a || !b) return false;
    if (a.selectAll !== b.selectAll) return false;
    if (a.selectAll) return true;
    const aSet = new Set(Array.isArray(a.sections) ? a.sections : []);
    const bSet = new Set(Array.isArray(b.sections) ? b.sections : []);
    if (aSet.size !== bSet.size) return false;
    for (const id of aSet) {
      if (!bSet.has(id)) return false;
    }
    return true;
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!pendingSelection) return false;
    const baseline = lastSyncedSelectionRef.current;
    if (!baseline) return false;
    return !selectionsEqual(pendingSelection, baseline);
  }, [pendingSelection]);

  React.useEffect(() => {
    if (!isEditingSelection) return;
    if (pendingSelection !== null) return;
    setPendingSelection(selectionSnapshot);
    lastSyncedSelectionRef.current = selectionSnapshot;
  }, [isEditingSelection, pendingSelection, selectionSnapshot]);

  // Keep pending selection in sync if the parent changes selection,
  // but ONLY when the user hasn't made local (unsaved) edits.
  React.useEffect(() => {
    if (!pendingSelection) return;
    const lastSynced = lastSyncedSelectionRef.current;
    if (lastSynced && selectionsEqual(pendingSelection, lastSynced) && !selectionsEqual(selectionSnapshot, lastSynced)) {
      setPendingSelection(selectionSnapshot);
      lastSyncedSelectionRef.current = selectionSnapshot;
    }
  }, [pendingSelection, selectionSnapshot]);


  // Get all stations for the current farm from the selectedFarm data
  // Stations are now directly available in the farm object from mapMockData
  const farmStations = useMemo(() => {
    // Use stations from selectedFarm (which now comes from mapMockData)
    if (selectedFarm?.stations) {
      return selectedFarm.stations;
    }
    // Fallback to MOCK_STATIONS filter if stations not available
    if (!layoutFarm?.id) return [];
    return MOCK_STATIONS.filter(station => station.farmId === layoutFarm.id || station.projectId === layoutFarm.id);
  }, [selectedFarm?.stations, layoutFarm?.id]);

  const farmGrouping = useMemo(() => {
    if (!selectedFarm) {
      return {
        groupingLevel: 'section',
        areaHa: 0,
        bounds: null,
        sections: [],
        zones: [],
        quadrants: [],
      };
    }
    return buildFarmGrouping({
      farm: selectedFarm,
      sections: selectedFarm?.sections || [],
      stations: farmStations,
    });
  }, [selectedFarm, selectedFarm?.sections, farmStations]);

  const groupingSections = useMemo(() => {
    return Array.isArray(farmGrouping?.sections) ? farmGrouping.sections : [];
  }, [farmGrouping]);

  const selectorSections = useMemo(() => {
    return groupingSections.map(({ stations, ...section }) => section);
  }, [groupingSections]);

  const allSectionIds = useMemo(() => {
    return groupingSections.map(section => section.id).filter(Boolean);
  }, [groupingSections]);

  // Sections now already have positions from AnalyticsContext (based on real GPS coordinates)
  // Just use them directly, adding any additional computed data
  const sectionsWithPositions = useMemo(() => {
    if (!selectorSections.length) return [];

    return selectorSections.map((section) => {
      const position = section.position
        || (Array.isArray(section.center) ? { lat: section.center[0], lon: section.center[1] } : null)
        || (section.bounds ? {
          lat: (section.bounds.minLat + section.bounds.maxLat) / 2,
          lon: (section.bounds.minLon + section.bounds.maxLon) / 2,
        } : null)
        || { lat: 0, lon: 0 };
      
      return {
        ...section,
        position,
      };
    });
  }, [selectorSections]);

  const updatePendingSelection = React.useCallback((updates) => {
    setPendingSelection((prev) => {
      const base = prev || selectionSnapshot;
      return {
        ...base,
        ...updates,
      };
    });
  }, [selectionSnapshot]);

  // Handle section selection in map
  const handleMapSectionClick = (sectionId) => {
    if (!isEditingSelection) return;
    const currentSelection = pendingSelection || selectionSnapshot;

    if (currentSelection.selectAll) {
      // If all was selected, switch to specific selection
      const newSections = allSectionIds.filter(id => id !== sectionId);
      updatePendingSelection({ selectAll: false, sections: newSections });
      return;
    }
    const isSelected = currentSelection.sections.includes(sectionId);
    const nextSections = isSelected
      ? currentSelection.sections.filter(id => id !== sectionId)
      : [...currentSelection.sections, sectionId];
    updatePendingSelection({ selectAll: false, sections: nextSections });
  };

  // Handle save changes
  const handleSaveChanges = () => {
    if (!pendingSelection) return;

    // Apply pending selection to parent state, but keep Map view open.
    if (pendingSelection.selectAll) {
      onSelectAllChange(true);
      onSectionsChange(allSectionIds);
      const next = { selectAll: true, sections: allSectionIds };
      lastSyncedSelectionRef.current = next;
      setPendingSelection(next);
      setIsEditingSelection(false);
      setDrillState(null);
      return;
    }

    onSelectAllChange(false);
    onSectionsChange(pendingSelection.sections);
    const next = { selectAll: false, sections: [...pendingSelection.sections] };
    lastSyncedSelectionRef.current = next;
    setPendingSelection(next);
    setIsEditingSelection(false);
    setDrillState(null);
  };

  // Handle cancel changes
  const handleCancelChanges = () => {
    // Revert local edits back to the last synced (saved) selection, but keep Map view open.
    const baseline = lastSyncedSelectionRef.current;
    if (baseline) {
      setPendingSelection(baseline);
      return;
    }
    setPendingSelection({
      selectAll: selectAllSections,
      sections: [...selectedSectionsStable],
    });
  };

  const handleEditSelection = () => {
    const baseline = lastSyncedSelectionRef.current || {
      selectAll: selectAllSections,
      sections: [...selectedSectionsStable],
    };
    lastSyncedSelectionRef.current = baseline;
    setPendingSelection(baseline);
    setIsEditingSelection(true);
    setDrillState(null);
  };


  // Get stations from selected sections
  // Sections are field groups (Section A/B) and contain `stationIds`.
  const selectedStations = useMemo(() => {
    if (selectAllSections) {
      return farmStations;
    }
    
    if (selectedSections.length === 0) {
      return [];
    }

    const selectedSectionSet = new Set(selectedSections);
    return farmStations.filter((station) => {
      const ids = resolveSectionIdsForStation(selectedFarm, station.id, station, farmGrouping.sections);
      return ids.some(id => selectedSectionSet.has(id));
    });
  }, [selectedSections, selectAllSections, farmStations, selectedFarm, farmGrouping.sections]);

  const drillSectionIds = useMemo(() => {
    return getSectionIdsForDrill(farmGrouping, drillState);
  }, [farmGrouping, drillState]);

  const drillSectionSet = useMemo(() => {
    return new Set(drillSectionIds);
  }, [drillSectionIds]);

  const baseStations = useMemo(() => {
    if (selectAllSections || selectedSectionsStable.length === 0) {
      return farmStations;
    }
    return selectedStations;
  }, [selectAllSections, selectedSectionsStable.length, farmStations, selectedStations]);

  const visibleStations = useMemo(() => {
    if (drillSectionIds.length === 0) {
      return baseStations;
    }
    return baseStations.filter((station) => {
      const ids = resolveSectionIdsForStation(selectedFarm, station.id, station, farmGrouping.sections);
      return ids.some(id => drillSectionSet.has(id));
    });
  }, [baseStations, drillSectionIds, drillSectionSet, selectedFarm, farmGrouping.sections]);

  const treeSelection = useMemo(() => {
    if (isEditingSelection && pendingSelection) {
      return pendingSelection;
    }
    return selectionSnapshot;
  }, [isEditingSelection, pendingSelection, selectionSnapshot]);

  const treeStations = useMemo(() => {
    if (isEditingSelection) {
      return farmStations;
    }
    return visibleStations;
  }, [isEditingSelection, farmStations, visibleStations]);

  // Handle "All Stations" button
  const handleSelectAll = () => {
    if (isEditingSelection) {
      updatePendingSelection({ selectAll: true, sections: allSectionIds });
      setDrillState(null);
      return;
    }
    onSelectAllChange(true);
    onSectionsChange(allSectionIds);
    setDrillState(null);
  };

  // Handle "Clear Selection" button
  const handleClear = () => {
    if (isEditingSelection) {
      updatePendingSelection({ selectAll: false, sections: [] });
      setDrillState(null);
      return;
    }
    onSelectAllChange(false);
    onSectionsChange([]);
    setDrillState(null);
  };

  // Handle section click on map
  const handleSectionClick = (sectionId) => {
    console.log('Section clicked on map:', sectionId);
    
    // Toggle section selection
    if (selectAllSections) {
      // When "all" is selected, clicking a section should:
      // 1. Turn off "all" mode
      // 2. Select only the clicked section
      console.log('All sections selected, switching to single section:', sectionId);
      onSectionsChange([sectionId]);
      onSelectAllChange(false);
      return;
    }

    // When not in "all" mode, toggle the section
    const isCurrentlySelected = selectedSections.includes(sectionId);
    console.log('Section currently selected:', isCurrentlySelected);
    
    if (isCurrentlySelected) {
      // If section is already selected, deselect it
      const newSelection = selectedSections.filter(id => id !== sectionId);
      console.log('Deselecting section, new selection:', newSelection);
      onSectionsChange(newSelection);
    } else {
      // If section is not selected, add it to selection
      const newSelection = [...selectedSections, sectionId];
      console.log('Selecting section, new selection:', newSelection);
      onSectionsChange(newSelection);
    }
  };

  // Handle station click - section IDs now match station IDs directly
  const handleStationClick = (stationId) => {
    console.log('handleStationClick called:', stationId);
    
    // Section IDs now match station IDs, so we can use stationId directly as sectionId
    handleSectionClick(stationId);
  };

  const mapViewLabel = t('analytics.selector.mapView', { defaultValue: 'Map' });
  const treeViewLabel = t('analytics.selector.treeView', { defaultValue: 'Tree' });
  const showMapView = mapExpanded || mapClosing;
  const showTreeView = !hideTreeView && !mapExpanded && !mapClosing;
  const unsavedWarningVisible = hasUnsavedChanges;

  React.useEffect(() => {
    if (typeof onUnsavedChangesChange !== 'function') return;
    onUnsavedChangesChange(unsavedWarningVisible);
    return () => onUnsavedChangesChange(false);
  }, [onUnsavedChangesChange, unsavedWarningVisible]);

  if (!selectedFarm || !layoutFarm) {
    return null;
  }

  return (
    <div className="station-selection-header">
      <div className="station-selection-header__content">
        {showActions && (
          <div className="station-selection-header__actions">
            <Button
              variant={selectAllSections ? "primary" : "secondary"}
              onClick={handleSelectAll}
              size="sm"
              className="station-selection-header__button"
            >
              {t('common.all')}
            </Button>
            
            <Button
              variant="secondary"
              onClick={handleClear}
              disabled={selectedSections.length === 0 && !selectAllSections}
              size="sm"
              className="station-selection-header__button"
            >
              {t('common.clear')}
            </Button>
          </div>
        )}

        {showViewToggle && (
          <div className="station-selection-header__toggle-buttons">
            <button
              type="button"
              className={`station-selection-header__toggle-button ${mapExpanded ? 'station-selection-header__toggle-button--active' : ''}`}
              onClick={() => setMapView(true)}
              aria-pressed={mapExpanded}
            >
              <Map size={16} />
              <span>{mapViewLabel}</span>
            </button>
            <button
              type="button"
              className={`station-selection-header__toggle-button ${!mapExpanded ? 'station-selection-header__toggle-button--active' : ''}`}
              onClick={() => setMapView(false)}
              aria-pressed={!mapExpanded}
            >
              <Layers size={16} />
              <span>{treeViewLabel}</span>
            </button>
          </div>
        )}

        {showUnsavedWarning && unsavedWarningVisible && (
          <div className="station-selection-header__unsaved-warning">
            <AlertCircle size={16} className="station-selection-header__unsaved-icon" />
            <span className="station-selection-header__unsaved-text">{t('common.unsavedChangesShort')}</span>
          </div>
        )}
      </div>

      {showTreeView && (
        <div className="station-selection-header__tree-container">
          <HierarchicalSelector
            farm={selectedFarm}
            sections={selectorSections}
            stations={treeStations}
            selectedSections={treeSelection.sections}
            selectAllSections={treeSelection.selectAll}
            onSectionsChange={(sections) => {
              if (isEditingSelection) {
                updatePendingSelection({ sections });
                return;
              }
              onSectionsChange(sections);
            }}
            onSelectAllChange={(selectAll) => {
              if (isEditingSelection) {
                updatePendingSelection({ selectAll });
                return;
              }
              onSelectAllChange(selectAll);
            }}
            drillState={drillState}
            onDrillChange={setDrillState}
            selectionDisabled={!isEditingSelection}
            footerActions={(hasUnsavedChanges || !isEditingSelection) && (
              <div className={`station-selection-header__tree-actions ${!isEditingSelection ? 'station-selection-header__tree-actions--split' : ''}`}>
                {isEditingSelection && hasUnsavedChanges && (
                  <div className="station-selection-header__tree-buttons">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleCancelChanges}
                      className="station-selection-header__tree-button"
                    >
                      <X size={16} />
                      <span>{t('common.cancel')}</span>
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSaveChanges}
                      className="station-selection-header__tree-button"
                    >
                      <Save size={16} />
                      <span>{t('common.saveChanges')}</span>
                    </Button>
                  </div>
                )}
                {!isEditingSelection && (
                  <>
                    <div className="station-selection-header__tree-status" role="status">
                      <CheckCircle size={14} className="station-selection-header__tree-status-icon" />
                      <span>
                        {t('analytics.map.selectionSaved', {
                          defaultValue: 'Selection saved. Click Edit to change.',
                        })}
                      </span>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleEditSelection}
                      className="station-selection-header__tree-button"
                    >
                      <Edit size={16} />
                      <span>{t('analytics.map.editSelection', { defaultValue: 'Edit selection' })}</span>
                    </Button>
                  </>
                )}
              </div>
            )}
          />
        </div>
      )}

      {/* Expandable Map View */}
      {showMapView && (
        <div className={`station-selection-header__map-container ${mapClosing ? 'station-selection-header__map-container--closing' : ''}`}>
          <CompactMapView
            sections={sectionsWithPositions}
            selectedSections={pendingSelection?.sections ?? selectedSections}
            selectAllSections={pendingSelection?.selectAll ?? selectAllSections}
            onSectionClick={handleMapSectionClick}
            onSectionsChange={(sections) => {
              if (isEditingSelection) {
                updatePendingSelection({ sections });
                return;
              }
              onSectionsChange(sections);
            }}
            onSelectAllChange={(selectAll) => {
              if (isEditingSelection) {
                updatePendingSelection({ selectAll });
                return;
              }
              onSelectAllChange(selectAll);
            }}
            onSave={handleSaveChanges}
            onCancel={handleCancelChanges}
            hasUnsavedChanges={hasUnsavedChanges}
            isEditingSelection={isEditingSelection}
            onEditSelection={handleEditSelection}
            // Use Analytics farm object (includes polygon/bounds) so the map overlay
            // matches Map View behavior (Section A/B from farm polygon split).
            farm={selectedFarm}
            stations={visibleStations}
            drillState={drillState}
            onDrillChange={setDrillState}
          />
        </div>
      )}
    </div>
  );
}

StationSelectionHeader.propTypes = {
  selectedSections: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectAllSections: PropTypes.bool.isRequired,
  onSectionsChange: PropTypes.func.isRequired,
  onSelectAllChange: PropTypes.func.isRequired,
  mapExpanded: PropTypes.bool,
  mapClosing: PropTypes.bool,
  onMapToggle: PropTypes.func,
  showActions: PropTypes.bool,
  showViewToggle: PropTypes.bool,
  hideTreeView: PropTypes.bool,
  showUnsavedWarning: PropTypes.bool,
  onUnsavedChangesChange: PropTypes.func,
};

